import type { ReactNode } from 'react'
import React, { createContext, useContext, useMemo } from 'react'

import { SuspenseFallback } from '@fourthwall/components'

import { useCurrentShopContext } from '../hooks/useCurrentShopContext'
import { useShopQuery } from '../hooks/useShopQuery'

type Props = { children: ReactNode }

export type ShopContextType = {
  shop: NonNullable<ReturnType<typeof useShopQuery>['shopQuery']['data']>
  refetch: () => Promise<any>
}

export const ShopContext = createContext<ShopContextType | undefined>(undefined)

export const ShopProvider = ({ children }: Props) => {
  const { currentShop } = useCurrentShopContext()
  const { shopQuery } = useShopQuery([{ shop_id: currentShop.id }])

  const value = useMemo(() => {
    if (!shopQuery.data || shopQuery.isError) {
      return undefined
    }

    return { shop: shopQuery.data, refetch: shopQuery.refetch }
  }, [shopQuery])

  if (!value) {
    return <SuspenseFallback />
  }

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>
}

export const useShopContext = () => {
  const shopContext = useContext(ShopContext)

  if (shopContext === undefined) {
    throw new Error('shopContext must be used within a ShopProvider')
  }

  return shopContext
}
